import * as Sentry from "@sentry/react";
import { supabase } from "@/lib/supabase";
import i18n from "@/lib/i18n";
import { invalidateCollections, type Collection } from "@/hooks/useCollections";
import { compressImage } from "@/utils/compressImage";
import type { CardContext } from "@/types/cardContext";
import type { CaptureData } from "@/contexts/CaptureContext";
import type { ParticipantRef } from "@/types/participant";
import { fireEvent } from "@/lib/events";

// The payload a pluggable `processor` must return so ProcessingScreen can
// fan out shared post-save side effects (onMemorySaved streak/acorn path,
// feed refresh). Kept deliberately narrow — processors that need richer
// behaviour should perform it before returning.
//
// `skipFanout` is opted in by unauthenticated flows (contributor invite) that
// have no owning user to accrue streak/acorns to. Default is to run the
// fan-out.
export interface ProcessorResult {
  memoryId: string;
  memoryType: string;
  contentLibraryId: string | null;
  skipFanout?: boolean;
  forcedSkip?: boolean;
}

interface CaptureSaveArgs {
  data: CaptureData;
  activeCollection: Collection | null;
}

// ── Client-side post-save helpers ───────────────────────────────────────────
// All storage accounting, tier enforcement, memory insert, and memory_participants
// writes happen server-side in `process-memory` now. What stays on the client
// are the *card-driven* collection hooks — auto-creating a collection from a
// matching template card, and ticking off required template steps — because
// those rules live in code paths the edge function doesn't consult.

async function autoCreateCollectionIfNeeded(
  userId: string,
  card: CardContext,
  memoryId: string,
) {
  if (!card.contentLibraryId) return;
  try {
    const { data: templateRows } = await supabase
      .from("collection_template_steps")
      .select("collection_template_id")
      .eq("content_library_id", card.contentLibraryId);

    const templateIds = (templateRows ?? []).map((r: { collection_template_id: string }) => r.collection_template_id);
    if (templateIds.length === 0) return;

    const { data: existingCollection } = await supabase
      .from("collections")
      .select("id")
      .eq("user_id", userId)
      .in("template_id", templateIds)
      .order("last_entry_at", { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    if (!existingCollection) return;

    await supabase
      .from("collection_memories")
      .upsert(
        { collection_id: (existingCollection as { id: string }).id, memory_id: memoryId, added_by: "auto" },
        { onConflict: "collection_id,memory_id", ignoreDuplicates: true },
      );
    void invalidateCollections();
  } catch {
    /* best effort */
  }
}

async function checkCollectionCompletion(collectionId: string, userId: string): Promise<void> {
  const { data: collection } = await supabase
    .from("collections")
    .select("template_id, completed_at")
    .eq("id", collectionId)
    .single();

  if (!collection?.template_id) return;
  if (collection?.completed_at) return;

  const stepsResult = await supabase
    .from("collection_template_steps")
    .select("id")
    .eq("collection_template_id", collection.template_id)
    .eq("required", true);

  const requiredSteps = stepsResult.data as Array<{ id: string }> | null;
  if (!requiredSteps?.length) return;

  const { data: completions } = await supabase
    .from("collection_step_completions")
    .select("step_id")
    .eq("collection_id", collectionId)
    .in("step_id", requiredSteps.map((s) => s.id));

  if (completions?.length !== requiredSteps.length) return;

  const { data: updated } = await supabase
    .from("collections")
    .update({ completed_at: new Date().toISOString() })
    .eq("id", collectionId)
    .is("completed_at", null)
    .select("id");

  if (!updated?.length) return;

  fireEvent("collection.completed", {
    collectionId,
    userId,
    templateId: (collection as { template_id: string }).template_id,
  });
}

async function autoCompleteCollectionSteps(
  memoryId: string,
  contentLibraryId: string,
  userId: string,
): Promise<void> {
  const { data: steps } = await supabase
    .from("collection_template_steps")
    .select("id, collection_template_id, required, repeatable, cooldown_days, content_library_id")
    .eq("content_library_id", contentLibraryId);

  if (!steps?.length) return;

  for (const step of steps as Array<{
    id: string;
    collection_template_id: string;
    required: boolean;
    repeatable: boolean;
    cooldown_days: number | null;
    content_library_id: string;
  }>) {
    const { data: collections } = await supabase
      .from("collections")
      .select("id")
      .eq("template_id", step.collection_template_id)
      .eq("user_id", userId)
      .is("completed_at", null);

    if (!collections?.length) continue;

    if (step.repeatable) {
      // Repeatable steps never write a completion row — they stay in the upcoming
      // list so the user can capture them again. Apply a cooldown snooze when
      // configured. The card_snoozes unique index uses COALESCE(child_id, uuid-zero)
      // which Supabase JS onConflict cannot express, so we delete-then-insert.
      if (step.cooldown_days != null && step.cooldown_days > 0) {
        const snoozeUntil = new Date(Date.now() + step.cooldown_days * 86400000).toISOString();
        try {
          await supabase
            .from("card_snoozes")
            .delete()
            .eq("user_id", userId)
            .eq("content_id", contentLibraryId)
            .is("child_id", null);
          await supabase
            .from("card_snoozes")
            .insert({ user_id: userId, content_id: contentLibraryId, child_id: null, snooze_until: snoozeUntil });
        } catch {
          /* best effort */
        }
      }
      continue;
    }

    for (const collection of collections as Array<{ id: string }>) {
      const { error } = await supabase
        .from("collection_step_completions")
        .upsert(
          {
            collection_id: collection.id,
            step_id: step.id,
            memory_id: memoryId,
            completed_by: userId,
            auto_completed: true,
          },
          { onConflict: "collection_id,step_id", ignoreDuplicates: true },
        );

      if (error) continue;

      await checkCollectionCompletion(collection.id, userId);
    }
  }
}

// ── Unified save ────────────────────────────────────────────────────────────
// Uploads whatever artifacts this capture carries, then asks the unified
// `process-memory` edge function to insert the row, enforce tier caps, run
// Gemini, and link participants/collections. One path for every memory type.

async function uploadArtifacts(
  userId: string,
  data: CaptureData,
): Promise<{
  audio_path?: string;
  image_path?: string;
  image_paths?: string[];
  video_path?: string;
  thumbnail_path?: string;
}> {
  const out: {
    audio_path?: string;
    image_path?: string;
    image_paths?: string[];
    video_path?: string;
    thumbnail_path?: string;
  } = {};

  // Audio — VoiceCapture uploads to storage before calling completeCaptureStep,
  // so `audioStoragePath` is already a bucket-relative path. Just forward it.
  if (data.audioStoragePath) {
    out.audio_path = data.audioStoragePath;
  }

  // Image(s) — PhotoCapture hands us File objects; compress + upload here.
  // Multi-photo: imageFiles contains all selected files; imageFile is the first.
  // Single-photo fallback: only imageFile is set (legacy / drawing path).
  if (data.imageFiles?.length) {
    const batchId = crypto.randomUUID();
    const paths = await Promise.all(
      data.imageFiles.map(async (file, i) => {
        const compressed = await compressImage(file);
        const path = `${userId}/${batchId}_${i}.jpg`;
        const { error } = await supabase.storage
          .from("memory-images")
          .upload(path, compressed, { contentType: "image/jpeg" });
        if (error) throw new Error(`Image upload failed: ${error.message}`);
        return path;
      }),
    );
    out.image_path = paths[0];
    if (paths.length > 1) out.image_paths = paths;
  } else if (data.imageFile) {
    const compressed = await compressImage(data.imageFile);
    const path = `${userId}/${Date.now()}.jpg`;
    const { error } = await supabase.storage
      .from("memory-images")
      .upload(path, compressed, { contentType: "image/jpeg" });
    if (error) throw new Error(`Image upload failed: ${error.message}`);
    out.image_path = path;
  }

  // Video + thumbnail — ShortieCapture hands us Blobs; upload both here.
  if (data.videoBlob) {
    const tempId = crypto.randomUUID();
    const ext = data.videoMimeType?.includes("mp4") ? "mp4" : "webm";
    const videoPath = `${userId}/${tempId}.${ext}`;
    const thumbPath = data.thumbnailBlob
      ? `${userId}/${tempId}_thumb.jpg`
      : null;

    const [videoRes, thumbRes] = await Promise.all([
      supabase.storage
        .from("videos")
        .upload(videoPath, data.videoBlob, {
          contentType: data.videoMimeType ?? "video/webm",
        }),
      thumbPath && data.thumbnailBlob
        ? supabase.storage
            .from("memory-images")
            .upload(thumbPath, data.thumbnailBlob, { contentType: "image/jpeg" })
        : Promise.resolve({ error: null }),
    ]);
    if (videoRes.error) throw new Error(`Video upload failed: ${videoRes.error.message}`);
    if (thumbRes.error) throw new Error(`Thumbnail upload failed: ${thumbRes.error.message}`);
    out.video_path = videoPath;
    if (thumbPath) out.thumbnail_path = thumbPath;
  }

  return out;
}

function participantRefsToServer(
  participants: ParticipantRef[],
): Array<{ kind: "connection"; space_member_id: string } | { kind: "name"; name: string }> {
  const out: Array<
    { kind: "connection"; space_member_id: string } | { kind: "name"; name: string }
  > = [];
  for (const p of participants) {
    if (p.kind === "owner") continue; // owner is implicit via memories.user_id
    if (p.kind === "connection") {
      // Connected tribe members keep their space_member_id row; anonymous
      // contributors (profileId null) fall back to free-text display_name.
      if (p.profileId) {
        out.push({ kind: "connection", space_member_id: p.connectionId });
      } else {
        out.push({ kind: "name", name: p.name });
      }
    } else {
      out.push({ kind: "name", name: (p as { name: string }).name });
    }
  }
  return out;
}

async function saveMemory(
  userId: string,
  data: CaptureData,
  cardContext: CardContext | null,
): Promise<{ id: string; forcedSkip: boolean }> {
  // voice_moment / general_memory / time_capsule → 1:1 mapping. 'photo' /
  // 'drawing' / 'shortie' → direct. 'general_memory' with written text is
  // distinguished server-side by absence of audio_path.
  const memoryType = data.type === "time_capsule" ? "time_capsule" : data.type ?? "voice_moment";

  const artifacts = await uploadArtifacts(userId, data);

  const { data: profileData } = await supabase
    .from("profiles")
    .select("language, content_language")
    .eq("id", userId)
    .single();
  const profile = profileData as
    | { language?: string; content_language?: string }
    | null;
  const language = profile?.language ?? "en";
  const contentLanguage = profile?.content_language ?? language;

  // Written general_memory uses the typed text as context_text; server stores
  // it on raw_transcript and Gemini sees it as USER_CONTEXT.
  const contextText =
    data.contextText ?? (data.writtenText?.trim() || undefined);

  const payload: Record<string, unknown> = {
    memory_type: memoryType,
    ...artifacts,
    ...(data.contextAudioPath ? { context_audio_path: data.contextAudioPath } : {}),
    ...(contextText ? { context_text: contextText } : {}),
    ...(typeof data.audioDurationSeconds === "number"
      ? { audio_duration_seconds: data.audioDurationSeconds }
      : {}),
    ...(typeof data.videoDuration === "number"
      ? { video_duration_seconds: data.videoDuration }
      : {}),
    ...(data.imageFiles?.length ? { photo_count: data.imageFiles.length } : data.imageFile ? { photo_count: 1 } : {}),
    child_ids: data.childIds,
    participants: participantRefsToServer(data.participants),
    location: data.location || null,
    memory_date: data.memoryDate.toISOString(),
    capsule_state: data.capsuleState,
    ...(data.audience ? { audience: data.audience } : {}),
    ...(data.approvalStatus === 'private' ? { approval_status: 'private' } : {}),
    content_library_id: cardContext?.contentLibraryId || null,
    card_tags: cardContext?.tags ?? [],
    language,
    content_language: contentLanguage,
    // collectionId semantics preserved: undefined = active-mode fallback,
    // null = explicit opt-out, string = picker choice.
    ...("collectionId" in data ? { collection_id: data.collectionId ?? null } : {}),
    // Gate output: 'full' | 'transcript_only' | 'skip'. Omitted for legacy
    // (null) — edge fn treats absence as 'full'.
    ...(data.processingMode ? { processing_mode: data.processingMode } : {}),
  };

  const { data: result, error } = await supabase.functions.invoke(
    "process-memory",
    { body: payload },
  );
  if (error) {
    // 409 already_enriched: memory was already processed (e.g. retry after network
    // glitch). 409 retry_exhausted: edge fn gave up after N attempts. Both are
    // recoverable — use the memoryId from the body if present so the UI can
    // navigate to the existing memory without losing the capture.
    if ("context" in error && error.context instanceof Response) {
      let specificError: Error | null = null;
      try {
        const body = await (error.context as Response).json();
        const code = (body as { code?: string })?.code;
        if (code === "already_enriched" || code === "retry_exhausted") {
          const id =
            (body as { memory_id?: string; memoryId?: string })?.memory_id ??
            (body as { memory_id?: string; memoryId?: string })?.memoryId;
          if (id) return { id, forcedSkip: false };
        }
        if (code === "PHOTO_COUNT_EXCEEDS_TIER") {
          const max = (body as { max_photos?: number })?.max_photos;
          specificError = new Error(i18n.t("capture.errors.photoCountExceeded", { max: max ?? "?" }));
        } else if (code === "voice_duration_exceeded") {
          const seconds = (body as { max_seconds?: number })?.max_seconds;
          specificError = new Error(i18n.t("capture.errors.voiceDurationExceeded", { seconds: seconds ?? "?" }));
        } else if (code === "video_duration_exceeded") {
          const seconds = (body as { max_seconds?: number })?.max_seconds;
          specificError = new Error(i18n.t("capture.errors.videoDurationExceeded", { seconds: seconds ?? "?" }));
        } else if (code === "voice_context_not_allowed") {
          specificError = new Error(i18n.t("capture.errors.voiceContextNotAllowed"));
        } else if (code === "voice_weekly_cap_reached") {
          const cap = (body as { max_per_week?: number })?.max_per_week;
          specificError = new Error(i18n.t("capture.errors.voiceWeeklyCapReached", { cap: cap ?? "?" }));
        } else if (code === "video_weekly_cap_reached") {
          const cap = (body as { max_per_week?: number })?.max_per_week;
          specificError = new Error(i18n.t("capture.errors.videoWeeklyCapReached", { cap: cap ?? "?" }));
        } else if (code === "storage_exceeded") {
          specificError = new Error(i18n.t("capture.errors.storageExceeded"));
        } else if (code) {
          specificError = new Error(i18n.t("capture.errors.processingFailedGeneric"));
        }
      } catch { /* non-JSON body — fall through */ }
      if (specificError) throw specificError;
    }
    throw new Error(i18n.t("capture.errors.processingFailedGeneric"));
  }
  const r = result as { memoryId?: string; forced_skip?: boolean } | null;
  const id = r?.memoryId;
  if (!id) throw new Error("No memory id returned");
  return { id, forcedSkip: r?.forced_skip === true };
}

// Re-runs AI enrichment on an already-saved memory. Only the memory_id is
// sent; the edge function skips upload and cap checks entirely.
export async function retryAiProcessing(
  memoryId: string,
): Promise<{ status: "ok" | "already_enriched" | "exhausted" }> {
  const { data, error } = await supabase.functions.invoke("process-memory", {
    body: { memory_id: memoryId },
  });
  if (error) {
    if ("context" in error && error.context instanceof Response) {
      try {
        const body = await (error.context as Response).json();
        const code = (body as { code?: string })?.code;
        if (code === "already_enriched") return { status: "already_enriched" };
        if (code === "retry_exhausted") return { status: "exhausted" };
      } catch { /* non-JSON body — fall through */ }
    }
    throw error;
  }
  const code = (data as { code?: string } | null)?.code;
  if (code === "already_enriched") return { status: "already_enriched" };
  return { status: "ok" };
}

// Full capture save pipeline used by the in-app capture flow. Returns the
// payload WaitScreen needs to dispatch shared post-save effects.
export async function runCaptureSave(args: CaptureSaveArgs): Promise<ProcessorResult> {
  const { data } = args;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");

  const cardContext = data.cardContext;
  const memoryType =
    data.type === "time_capsule" ? "voice_moment" : (data.type ?? "voice_moment");

  const { id: insertedMemoryId, forcedSkip } = await saveMemory(user.id, data, cardContext);

  // Card-driven post-save orchestration. The server handled tier, insert,
  // participants, challenge_completions, and the active-mode collection link
  // — what remains are client-only rules that live in Mori's feed logic:
  // auto-creating a template-backed collection from a matching card, and
  // ticking required template steps.
  const postSaveTasks: Array<Promise<unknown>> = [];
  if (cardContext?.contentLibraryId) {
    // Only fire the card-driven auto-create when the picker was never
    // involved (collectionId undefined). Explicit null = user opted out →
    // skip. Explicit string = server already linked — skip.
    if (data.collectionId === undefined) {
      postSaveTasks.push(
        autoCreateCollectionIfNeeded(user.id, cardContext, insertedMemoryId).catch(
          (err) => {
            console.error("[captureProcessor] autoCreateCollectionIfNeeded failed:", err);
            Sentry.captureException(err, {
              tags: { flow: "collection-auto-create", memoryId: insertedMemoryId },
            });
          },
        ),
      );
    }
    postSaveTasks.push(
      autoCompleteCollectionSteps(insertedMemoryId, cardContext.contentLibraryId, user.id).catch(
        (err) => {
          console.error("[captureProcessor] autoCompleteCollectionSteps failed:", err);
          Sentry.captureException(err, {
            tags: { flow: "collection-step-completion", memoryId: insertedMemoryId },
          });
        },
      ),
    );
  }
  await Promise.allSettled(postSaveTasks);

  // Always invalidate after a save — the server may have linked to an active
  // collection; card-driven auto-create above may have linked too.
  void invalidateCollections();

  return {
    memoryId: insertedMemoryId,
    memoryType,
    contentLibraryId: cardContext?.contentLibraryId ?? null,
    forcedSkip,
  };
}
